import React, { useCallback, useRef } from "react";

import type { NewProductDraftSnapshot, NewProductState, SourceConnection, SourceId } from "../newProduct/state";
import { NewProductScreen, type NewProductFormHandle } from "../newProduct/NewProductScreen";
import {
    addContextMeshSource,
    completeContextMeshCreationDraft,
    ContextMeshApiError,
    createContextMesh,
    getContextMesh,
    updateContextMesh,
    type CreateContextMeshRequest,
    type CreateContextMeshSourceRequest
} from "./contextMeshApi";

const GIT_PROVIDERS: Partial<Record<SourceId, string>> = {
    github: "github",
    gitlab: "gitlab",
    bitbucket: "bitbucket",
    azuregit: "azure-repos"
};

const INTEGRATION_PROVIDERS: Partial<Record<SourceId, string>> = {
    jira: "jira",
    azure: "azure-devops"
};

export type StandaloneContextMeshCreationType = "new_product" | "new_features" | "new_single_feature";

export interface PendingStandaloneContextMeshSubmission {
    contextMeshId: string;
    draftId: number;
    originalPayload: CreateContextMeshRequest;
    lastKnownPayload: CreateContextMeshRequest;
    patchIntent?: CreateContextMeshRequest;
    sourceSelection: CreateContextMeshSourceRequest[];
    sourceRequests: CreateContextMeshSourceRequest[];
}

export interface StandaloneContextMeshSubmissionDependencies {
    createWorkspace: typeof createContextMesh;
    addSource: typeof addContextMeshSource;
    getWorkspace: typeof getContextMesh;
    updateWorkspace: typeof updateContextMesh;
}

const defaultSubmissionDependencies: StandaloneContextMeshSubmissionDependencies = {
    createWorkspace: createContextMesh,
    addSource: addContextMeshSource,
    getWorkspace: getContextMesh,
    updateWorkspace: updateContextMesh
};

export class StandaloneContextMeshSourceError extends Error {
    readonly pending: PendingStandaloneContextMeshSubmission;

    constructor(pending: PendingStandaloneContextMeshSubmission) {
        const count = pending.sourceRequests.length;
        super(`${count} Context Mesh source${count === 1 ? "" : "s"} could not be attached. Select Create Context Mesh to retry.`);
        this.name = "StandaloneContextMeshSourceError";
        this.pending = pending;
    }
}

function normalizedRows(rows: string[]): string[] {
    return rows.map(value => value.trim()).filter(Boolean);
}

export function standaloneCreationType(state: NewProductState): StandaloneContextMeshCreationType {
    if (state.kind !== "existing") return "new_product";
    return state.scope === "single" ? "new_single_feature" : "new_features";
}

/**
 * Preserve every discovery answer from the shared creation form on the
 * standalone workspace. The first-class workspace columns feed Context Mesh
 * analysis; metadata retains the creation intent and existing-product context
 * without creating or linking a Product.
 */
export function buildStandaloneContextMeshRequest(state: NewProductState): CreateContextMeshRequest {
    const name = state.fields.name.trim();
    const problem = state.fields.problem.trim();
    const solution = state.fields.solution.trim();
    const personas = normalizedRows(state.users);
    const teamMembers = normalizedRows(state.team);
    const creationType = standaloneCreationType(state);
    const existingProduct = state.kind === "existing"
        ? {
              name: state.existingProduct.name.trim(),
              purpose: state.existingProduct.purpose.trim(),
              users: normalizedRows(state.existingProduct.users),
              capabilities: state.existingProduct.capabilities.trim()
          }
        : null;

    return {
        name,
        description: solution,
        problemStatement: problem,
        solutionStatement: solution,
        personas,
        teamComposition: teamMembers.join("\n"),
        nonfunctionalRequirements: Object.fromEntries(
            Object.entries(state.nfr).filter(([, value]) => value.trim()).map(([key, value]) => [key, value.trim()])
        ),
        metadata: {
            schemaVersion: 1,
            createdFrom: "context_mesh_hub",
            creationType,
            creationMode: "standalone_context_mesh",
            primaryFormFactor: state.primaryFormFactor ?? "web",
            featureScope: state.kind === "existing" ? state.scope : null,
            discovery: {
                name,
                problem,
                solution,
                targetUsers: personas,
                teamMembers,
                ...(existingProduct ? { existingProduct } : {})
            }
        }
    };
}

export function buildStandaloneSourceRequests(
    connections: Record<SourceId, SourceConnection>
): CreateContextMeshSourceRequest[] {
    const requests: CreateContextMeshSourceRequest[] = [];

    for (const fileId of connections.files.staged?.fileIds ?? []) {
        requests.push({ sourceKind: "vector_file", provider: "internal", vectorFileId: fileId });
    }

    for (const source of Object.keys(GIT_PROVIDERS) as SourceId[]) {
        const provider = GIT_PROVIDERS[source];
        if (!provider) continue;
        for (const repositoryId of connections[source].staged?.repoIds ?? []) {
            requests.push({ sourceKind: "git_repository", provider, repositoryId });
        }
    }

    for (const source of Object.keys(INTEGRATION_PROVIDERS) as SourceId[]) {
        const provider = INTEGRATION_PROVIDERS[source];
        if (!provider) continue;
        const connection = connections[source];
        for (const selection of connection.selection ?? []) {
            requests.push({
                sourceKind: "integration",
                provider,
                externalRef: selection.key || selection.id,
                externalName: selection.name,
                configuration: {
                    selectionId: selection.id,
                    ...(selection.key ? { selectionKey: selection.key } : {}),
                    ...(connection.staged?.processJobId ? { processJobId: connection.staged.processJobId } : {})
                },
                integrationMetadata: {
                    importedCount: connection.staged?.importedCount ?? 0
                }
            });
        }
    }

    return requests;
}

// Compare JSON structurally so metadata key ordering does not create a conflict.
function sameCreationValue(left: unknown, right: unknown): boolean {
  if (Object.is(left, right)) return true;
  if (Array.isArray(left) && Array.isArray(right)) {
    return left.length === right.length && left.every((value, index) => sameCreationValue(value, right[index]));
  }
  const l = creationRecord(left);
  const r = creationRecord(right);
  return !!l && !!r && Object.keys(l).length === Object.keys(r).length
    && Object.keys(l).every(key => Object.hasOwn(r, key) && sameCreationValue(l[key], r[key]));
}

function creationRecord(value: unknown): Record<string, unknown> | undefined {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown> : undefined;
}

function mergeCreationMetadata(
  current: Record<string, unknown>,
  previous: Record<string, unknown>,
  desired: Record<string, unknown>,
  attempted: Record<string, unknown> = {},
): Record<string, unknown> {
  const merged = { ...current };
  for (const key of new Set([...Object.keys(previous), ...Object.keys(desired), ...Object.keys(attempted)])) {
    const c = current[key]; const p = previous[key]; const d = desired[key]; const a = attempted[key];
    // Only creation-owned keys are reconciled; server additions remain intact.
    if (creationRecord(c) && creationRecord(p) && creationRecord(d)) {
      merged[key] = mergeCreationMetadata(creationRecord(c)!, creationRecord(p)!, creationRecord(d)!, creationRecord(a));
    } else {
      if (!sameCreationValue(c, p) && !sameCreationValue(c, d)
        && !(Object.hasOwn(attempted, key) && sameCreationValue(c, a))) {
        throw new Error('The pending Context Mesh Discover snapshot changed elsewhere. Restore those answers or resolve the conflict before retrying.');
      }
      if (Object.hasOwn(desired, key)) merged[key] = d;
      else delete merged[key];
    }
  }
  return merged;
}

function creationRetryPatch(
  workspace: { contextMeshId: string; version: number; accessRole: string; lifecycleStatus: string; metadata?: Record<string, unknown> },
  pending: { contextMeshId: string; draftId: number; lastKnownPayload: Record<string, unknown>; patchIntent?: Record<string, unknown> },
  desired: Record<string, unknown>,
): Record<string, unknown> | null {
  if (workspace.contextMeshId !== pending.contextMeshId || workspace.accessRole !== 'owner'
    || workspace.lifecycleStatus !== 'active' || !Number.isSafeInteger(workspace.version) || workspace.version <= 0) {
    throw new Error('The pending Context Mesh workspace changed or is no longer owned by you. Resolve the conflict before retrying.');
  }
  const metadata = workspace.metadata ?? {};
  if (metadata.sourceDraftId != null && String(metadata.sourceDraftId) !== String(pending.draftId)) {
    throw new Error('The pending Context Mesh draft binding changed. Resolve the conflict before retrying.');
  }
  const patch: Record<string, unknown> = {};
  const current = workspace as unknown as Record<string, unknown>;
  for (const key of ['name', 'description', 'problemStatement', 'solutionStatement', 'personas', 'teamComposition', 'nonfunctionalRequirements']) {
    const c = current[key] ?? (key === 'personas' ? [] : key === 'nonfunctionalRequirements' ? {} : '');
    const p = pending.lastKnownPayload[key]; const d = desired[key];
    if (!sameCreationValue(c, p) && !sameCreationValue(c, d)
      && !(pending.patchIntent && sameCreationValue(c, pending.patchIntent[key]))) {
      throw new Error('The pending Context Mesh Discover snapshot changed elsewhere. Restore those answers or resolve the conflict before retrying.');
    }
    if (!sameCreationValue(c, d)) patch[key] = d;
  }
  const mergedMetadata = mergeCreationMetadata(metadata, creationRecord(pending.lastKnownPayload.metadata) ?? {},
    creationRecord(desired.metadata) ?? {}, creationRecord(pending.patchIntent?.metadata));
  if (!sameCreationValue(mergedMetadata, metadata)) patch.metadata = mergedMetadata;
  return Object.keys(patch).length > 0 ? { ...patch, expectedVersion: workspace.version } : null;
}

/** Retain the immutable draft/workspace identity and reconcile only this creation's Discover edits. */
export async function submitStandaloneContextMesh(
    token: string,
    state: NewProductState,
    cached: PendingStandaloneContextMeshSubmission | null = null,
    dependencies: StandaloneContextMeshSubmissionDependencies = defaultSubmissionDependencies,
    draftId?: number,
    savePending: (pending: PendingStandaloneContextMeshSubmission) => void = () => undefined
): Promise<string> {
    if (!Number.isSafeInteger(draftId) || !draftId || draftId <= 0) {
        throw new Error("A saved Context Mesh draft identity is required before workspace creation.");
    }
    const desired = buildStandaloneContextMeshRequest(state);
    const selection = buildStandaloneSourceRequests(state.connections);
    let pending: PendingStandaloneContextMeshSubmission;
    if (cached) {
        if (!cached.draftId || !cached.originalPayload || !cached.lastKnownPayload || !Array.isArray(cached.sourceSelection)) {
            throw new Error("This legacy pending Context Mesh cache has no safe draft identity or Discover snapshot. Resolve the pending workspace before creating again.");
        }
        if (cached.draftId !== draftId) throw new Error("This pending Context Mesh belongs to a different draft. Resume its original draft before retrying.");
        if (!sameCreationValue(cached.sourceSelection, selection)) {
            throw new Error("Source selections changed after workspace creation. Restore the original sources before retrying this pending Context Mesh.");
        }
        pending = cached;
        const workspace = await dependencies.getWorkspace(token, pending.contextMeshId);
        const patch = creationRetryPatch(workspace, pending as unknown as Parameters<typeof creationRetryPatch>[1], desired as unknown as Record<string, unknown>);
        if (patch) {
            pending = { ...pending, patchIntent: desired };
            savePending(pending);
            await dependencies.updateWorkspace(token, pending.contextMeshId, patch);
        }
        pending = { ...pending, lastKnownPayload: desired, patchIntent: undefined };
        savePending(pending);
    } else {
        const workspace = await dependencies.createWorkspace(token, desired);
        pending = { contextMeshId: workspace.contextMeshId, draftId, originalPayload: desired,
            lastKnownPayload: desired, sourceSelection: selection, sourceRequests: selection };
        savePending(pending);
    }

    const failures: CreateContextMeshSourceRequest[] = [];
    for (const request of [...pending.sourceRequests]) {
        try {
            await dependencies.addSource(token, pending.contextMeshId, request);
        } catch (error) {
            // The source uniqueness conflict also covers a lost success response.
            if (!(error instanceof ContextMeshApiError && error.status === 409)) {
                console.error("Failed to attach Context Mesh source", error);
                failures.push(request);
                continue;
            }
        }
        pending = { ...pending, sourceRequests: pending.sourceRequests.filter(source => source !== request) };
        savePending(pending);
    }
    if (failures.length > 0) {
        pending = { ...pending, sourceRequests: failures };
        savePending(pending);
        throw new StandaloneContextMeshSourceError(pending);
    }
    return pending.contextMeshId;
}

export interface StandaloneContextMeshCreationScreenProps {
    token?: string | null;
    /** Resume an existing standalone Context Mesh draft. */
    draftId?: number | null;
    onCancel: () => void;
    onCreated: (contextMeshId: string) => void;
    onDirtyChange?: (dirty: boolean) => void;
    onDraftIdentityChange?: (draftId: number) => void;
    /** Exposes the shared form's live draft to FDE while this route is active. */
    draftRef?: React.MutableRefObject<NewProductDraftSnapshot | null>;
    /** Lets FDE apply a user-confirmed patch through the shared form reducer. */
    formRef?: React.MutableRefObject<NewProductFormHandle | null>;
}

/** Thin offering-specific adapter around the shared Add New Product form. */
export function StandaloneContextMeshCreationScreen({
    token,
    draftId,
    onCancel,
    onCreated,
    onDirtyChange,
    onDraftIdentityChange,
    draftRef,
    formRef
}: StandaloneContextMeshCreationScreenProps) {
    const pendingSubmissionRef = useRef<PendingStandaloneContextMeshSubmission | null>(null);
    // Once mounted, autosave may publish a newly assigned id to the route.
    // Keep the form's resume input fixed so that route update does not reload
    // (or remount) the draft the user is actively editing.
    const resumedDraftIdRef = useRef(draftId ?? null);

    const handleCreate = useCallback(async (
        state: NewProductState,
        persisted: { draftId: number; draftVersion: number }
    ) => {
        if (!token) throw new Error("Your session hasn't finished loading — try again in a moment.");
        try {
            const contextMeshId = await submitStandaloneContextMesh(
                token, state, pendingSubmissionRef.current, defaultSubmissionDependencies,
                persisted.draftId, pending => { pendingSubmissionRef.current = pending; }
            );
            await completeContextMeshCreationDraft(
                token,
                contextMeshId,
                persisted.draftId,
                persisted.draftVersion
            );
            pendingSubmissionRef.current = null;
            return contextMeshId;
        } catch (error) {
            if (error instanceof StandaloneContextMeshSourceError) {
                pendingSubmissionRef.current = error.pending;
            }
            throw error;
        }
    }, [token]);

    return (
        <NewProductScreen
            ref={formRef}
            draftRef={draftRef}
            creationExperience="standalone-context-mesh"
            draftId={resumedDraftIdRef.current}
            onCancel={onCancel}
            onCreated={() => undefined}
            onCreateStandaloneContextMesh={handleCreate}
            onStandaloneContextMeshCreated={onCreated}
            onDirtyChange={onDirtyChange}
            onDraftIdentityChange={onDraftIdentityChange}
        />
    );
}
